/**
 * The job application form: its fields, which are required, the employer-selectable STANDARD
 * screening questions, and the validation both sides run.
 *
 * One definition on purpose. The multi-step form (src/pages/emt/JobApplication.tsx) marks a field
 * with an asterisk from APPLICATION_REQUIRED_CONTACT_FIELDS, validates each step with
 * validateApplicationStep(), and the applyToJob Cloud Function re-validates and cleans the very
 * same payload with sanitizeApplicationSubmission() — so "asterisk = required" is always true, and
 * what the employer reads is what the server accepted, not what a client claimed.
 *
 * COMPLIANCE — read before changing anything here:
 * Standard screening answers are STORED and SHOWN to the employer exactly as submitted. Nothing in
 * this codebase may score, rank, filter, auto-reject, colour-code or otherwise derive a pass/fail
 * signal from them. Questions about drug testing and criminal background checks carry real legal
 * exposure (FCRA, state and city "ban the box" / fair-chance laws, ADA and EEOC guidance) that
 * varies by where the employer hires; every question is OFF unless the employer turns it on for a
 * job, and the legally sensitive ones are flagged in the employer UI.
 *
 * Dependency-free: shared with functions/ via scripts/syncFunctionsGenerated.mjs.
 */

/* ------------------------------------------------------------------ standard questions */

export type StandardScreeningKey =
  | 'work_authorization'
  | 'age_18'
  | 'essential_functions'
  | 'drug_test'
  | 'background_check'
  | 'start_date'
  | 'hours_seeking'
  | 'currently_employed'
  | 'reason_for_leaving'
  | 'education_history'
  | 'current_enrollment'
  | 'references';

export type StandardScreeningQuestion = {
  key: StandardScreeningKey;
  /** What the candidate is asked. */
  prompt: string;
  /** Short name in the employer's on/off list. */
  employerLabel: string;
  /** 'text' is one line; 'longtext' is a paragraph (references, education history). */
  type: 'yesno' | 'text' | 'longtext' | 'choice';
  options?: readonly string[];
  /**
   * A conditional question ("If presently employed…", "If currently enrolled…") that does not apply
   * to every candidate: it carries NO asterisk and may be left blank. Every other enabled question
   * needs an answer.
   */
  optional?: boolean;
  /**
   * The answer is about the candidate, not about this job, so they may choose to keep it on their
   * profile and have it filled into future applications (applicationAutoFill.ts) — reviewed and
   * editable before anything is sent. Never set this on a yes/no or legally sensitive question.
   */
  reusable?: boolean;
  /** Placeholder shown in the candidate's input. */
  placeholder?: string;
  /** A reusable question in a few of the candidate's words ("your references"), for the save offer. */
  shortLabel?: string;
  /** Carries heightened legal exposure; the employer UI says so next to the toggle. */
  legallySensitive?: boolean;
  /** Shown to the employer beside a legally sensitive toggle. */
  employerNote?: string;
};

export const HOURS_SEEKING_OPTIONS = [
  'Full-time (36+ hours a week)',
  'Part-time (20–35 hours a week)',
  'Under 20 hours a week',
  'Per diem / as needed',
] as const;

export const STANDARD_SCREENING_QUESTIONS: readonly StandardScreeningQuestion[] = [
  {
    key: 'work_authorization',
    prompt: 'Are you legally eligible for employment in the United States?',
    employerLabel: 'Legally employable in the US',
    type: 'yesno',
  },
  {
    key: 'age_18',
    prompt: 'Are you 18 years of age or older?',
    employerLabel: '18 or older',
    type: 'yesno',
  },
  {
    key: 'essential_functions',
    // ADA-standard phrasing: asks about the job's functions, never about a condition.
    prompt: 'Can you perform the essential functions of this role, with or without reasonable accommodation?',
    employerLabel: 'Can perform the functional requirements of the role',
    type: 'yesno',
  },
  {
    key: 'drug_test',
    // Asks about willingness to be tested — not whether the candidate would "pass", which reaches
    // lawful off-duty conduct and prescribed medication.
    prompt: 'Are you willing to take a pre-employment drug test?',
    employerLabel: 'Drug test',
    type: 'yesno',
    legallySensitive: true,
    employerNote:
      'Drug-testing rules vary by state (including protections for lawful cannabis use and prescribed medication). Get legal review before asking this.',
  },
  {
    key: 'background_check',
    prompt: 'If you are offered this position, will you consent to a criminal background check?',
    employerLabel: 'Criminal background check consent',
    type: 'yesno',
    legallySensitive: true,
    employerNote:
      'Many states and cities ("ban the box" / fair-chance laws) restrict raising criminal history before a conditional offer, and the FCRA requires its own separate disclosure and consent. Get legal review before asking this.',
  },
  {
    key: 'start_date',
    prompt: 'When can you begin?',
    employerLabel: 'Available start date',
    type: 'text',
  },
  {
    key: 'hours_seeking',
    prompt: 'How many hours per week are you seeking?',
    employerLabel: 'Hours sought',
    type: 'choice',
    options: HOURS_SEEKING_OPTIONS,
  },
  {
    key: 'currently_employed',
    prompt: 'Are you currently employed?',
    employerLabel: 'Currently employed',
    type: 'yesno',
  },
  {
    key: 'reason_for_leaving',
    prompt: 'If you are presently employed, why are you considering leaving?',
    employerLabel: 'Reason for leaving current employer',
    type: 'longtext',
    optional: true,
    reusable: true,
    shortLabel: 'why you are leaving',
  },
  {
    key: 'education_history',
    prompt: 'Give a brief summary of your education history, including the highest level you completed.',
    employerLabel: 'Education history',
    type: 'longtext',
    reusable: true,
    shortLabel: 'your education history',
    placeholder: 'e.g., High school diploma, 2019. EMT-Basic certificate, community college, 2021.',
  },
  {
    key: 'current_enrollment',
    prompt: 'If you are currently enrolled in school, what is your major and expected graduation date?',
    employerLabel: 'Current enrollment (major and graduation date)',
    type: 'text',
    optional: true,
    reusable: true,
    shortLabel: 'your current enrollment',
    placeholder: 'e.g., Paramedic A.A.S., graduating May 2027',
  },
  {
    key: 'references',
    prompt: 'Provide at least three professional references, with their contact information and their relationship to you.',
    employerLabel: 'Professional references (at least 3)',
    type: 'longtext',
    reusable: true,
    shortLabel: 'your references',
    placeholder: 'Name, relationship to you, phone or email. One reference per line.',
  },
];

const STANDARD_BY_KEY = new Map<string, StandardScreeningQuestion>(STANDARD_SCREENING_QUESTIONS.map((q) => [q.key, q]));

export function getStandardScreeningQuestion(key: string): StandardScreeningQuestion | undefined {
  return STANDARD_BY_KEY.get(key);
}

/** The questions whose answers a candidate may keep on their profile for future applications. */
export const REUSABLE_SCREENING_KEYS: readonly StandardScreeningKey[] = STANDARD_SCREENING_QUESTIONS.filter((q) => q.reusable).map(
  (q) => q.key,
);

/**
 * "Use the standard screening set": the one-click starting point an EMS HR team asked for (MedEx).
 * Turning it on enables exactly these, in canonical order; the employer can still switch any of them
 * off. Two of them are legally sensitive and keep their legal notes in the employer UI.
 */
export const STANDARD_SCREENING_SET_KEYS: readonly StandardScreeningKey[] = [
  'work_authorization',
  'age_18',
  'drug_test',
  'background_check',
  'start_date',
  'hours_seeking',
];

/* ------------------------------------------------------------------ the employer's own questions */

/**
 * The answer types an employer can give their own questions. 'text' is a short answer (one line,
 * up to 300 characters). 'likert' (a 1 to 5 rating) is LEGACY: no longer offered for a new question,
 * but a job that already has one keeps asking it and the employer keeps seeing the answers.
 */
export type CustomQuestionType = 'text' | 'longtext' | 'yesno' | 'choice' | 'date' | 'likert';

export const CUSTOM_QUESTION_TYPES: readonly CustomQuestionType[] = ['text', 'longtext', 'yesno', 'choice', 'date', 'likert'];

export const CUSTOM_QUESTION_TYPE_LABELS: Record<CustomQuestionType, string> = {
  text: 'Short answer',
  longtext: 'Long answer',
  yesno: 'Yes or no',
  choice: 'Multiple choice',
  date: 'Date',
  likert: '1 to 5 rating',
};

/** What the builder offers for a question (likert is left out on purpose, see CustomQuestionType). */
export const NEW_CUSTOM_QUESTION_TYPES: readonly Exclude<CustomQuestionType, 'likert'>[] = ['text', 'longtext', 'yesno', 'choice', 'date'];

export const CUSTOM_QUESTION_LIMITS = {
  maxQuestions: 10,
  promptMax: 300,
  minOptions: 2,
  maxOptions: 10,
  optionMax: 80,
} as const;

export type CustomScreeningQuestion = {
  id: string;
  type: CustomQuestionType;
  prompt: string;
  required?: boolean;
  /** Multiple choice only: the answers the candidate picks one of. */
  options?: string[];
};

/**
 * "Use suggested questions": about the candidate's goals, not trick interview questions. Inserted
 * as ordinary questions the employer can reword, retype or delete.
 */
export const SUGGESTED_GOAL_QUESTIONS: ReadonlyArray<{ prompt: string; type: Exclude<CustomQuestionType, 'likert'> }> = [
  { prompt: 'Are you able to travel for this job if needed?', type: 'yesno' },
  { prompt: 'What are you looking for in this job?', type: 'longtext' },
  { prompt: 'Where do you want to be in 1 to 2 years?', type: 'longtext' },
  { prompt: 'Are you planning paramedic school, fire academy or anything else, and roughly when?', type: 'longtext' },
  { prompt: 'When can you start?', type: 'date' },
  { prompt: 'What shifts and days can you work?', type: 'text' },
];

/**
 * Every NEW job starts with these four, already filled in (Derek Knost, Trident EMS, 2026-10-14: "not
 * everybody's creative... at least three to four generic questions that everybody would want to know").
 * The employer can edit, remove or add more up to the limit. Like every employer question, the
 * answers are never scored and never used to filter anyone. Existing jobs keep the questions they have.
 */
export const DEFAULT_NEW_JOB_QUESTIONS: ReadonlyArray<{ prompt: string; type: Exclude<CustomQuestionType, 'likert'> }> = [
  { prompt: 'Are you able to travel for this job if needed?', type: 'yesno' },
  { prompt: 'What shifts and days can you work?', type: 'text' },
  { prompt: 'When can you start?', type: 'date' },
  { prompt: 'What are you looking for in this job?', type: 'longtext' },
];

/** The four default questions as ready questions for a new job's editor, each with a fresh id. */
export function defaultNewJobQuestions(newId: () => string): CustomScreeningQuestion[] {
  return DEFAULT_NEW_JOB_QUESTIONS.map((q) => ({ id: newId(), type: q.type, prompt: q.prompt, required: false }));
}

export function isCustomQuestionType(value: unknown): value is CustomQuestionType {
  return typeof value === 'string' && (CUSTOM_QUESTION_TYPES as readonly string[]).includes(value);
}

const samePrompt = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The suggested questions not already on the list (compared by wording, ignoring case), cut to the
 * room left under the maximum. Each comes back as a ready question with a fresh id from `newId`.
 */
export function suggestedQuestionsToAdd(
  existing: ReadonlyArray<{ prompt: string }>,
  newId: () => string,
): CustomScreeningQuestion[] {
  const room = Math.max(0, CUSTOM_QUESTION_LIMITS.maxQuestions - existing.length);
  return SUGGESTED_GOAL_QUESTIONS.filter((s) => !existing.some((q) => samePrompt(q.prompt ?? '', s.prompt)))
    .slice(0, room)
    .map((s) => ({ id: newId(), type: s.type, prompt: s.prompt, required: false }));
}

/** Trimmed, non-empty choice texts (blank rows the employer has not filled in are ignored). */
export function cleanChoiceOptions(options: unknown): string[] {
  if (!Array.isArray(options)) return [];
  return options.filter((o): o is string => typeof o === 'string').map((o) => o.trim()).filter(Boolean);
}

/**
 * A job's stored questions as something the apply form and the server can ask: well-formed entries
 * of a known type only (anything else is skipped, never guessed at). A multiple-choice question
 * without at least two choices cannot be answered, so it is skipped too. 'lenient' (the employer's
 * editor) keeps such a question so the employer can see and fix it.
 */
export function normalizeCustomQuestions(raw: unknown, mode: 'strict' | 'lenient' = 'strict'): CustomScreeningQuestion[] {
  if (!Array.isArray(raw)) return [];
  const out: CustomScreeningQuestion[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const q = item as Record<string, unknown>;
    if (typeof q.id !== 'string' || !q.id || !isCustomQuestionType(q.type)) continue;
    const prompt = typeof q.prompt === 'string' ? q.prompt : '';
    if (mode === 'strict' && !prompt.trim()) continue;
    const question: CustomScreeningQuestion = { id: q.id, type: q.type, prompt: mode === 'strict' ? prompt.trim() : prompt, required: q.required === true };
    if (q.type === 'choice') {
      const options = mode === 'strict' ? cleanChoiceOptions(q.options) : Array.isArray(q.options) ? q.options.filter((o): o is string => typeof o === 'string') : [];
      if (mode === 'strict' && options.length < CUSTOM_QUESTION_LIMITS.minOptions) continue;
      question.options = options;
    }
    out.push(question);
  }
  return out;
}

export type CustomQuestionDefinitionError = { questionId: string | null; message: string };

/**
 * The employer's own questions, checked before the job is saved (the builder shows these next to
 * each question; jobFormModel.validateJobForm refuses to save while any remain).
 */
export function customQuestionDefinitionErrors(questions: ReadonlyArray<CustomScreeningQuestion>): CustomQuestionDefinitionError[] {
  const L = CUSTOM_QUESTION_LIMITS;
  const errors: CustomQuestionDefinitionError[] = [];
  if (questions.length > L.maxQuestions) errors.push({ questionId: null, message: `You can ask up to ${L.maxQuestions} questions.` });
  questions.forEach((q, i) => {
    const n = i + 1;
    const prompt = typeof q.prompt === 'string' ? q.prompt.trim() : '';
    if (!prompt) errors.push({ questionId: q.id, message: `Question ${n} needs the question text.` });
    else if (prompt.length > L.promptMax) errors.push({ questionId: q.id, message: `Question ${n} must be ${L.promptMax} characters or fewer.` });
    if (!isCustomQuestionType(q.type)) errors.push({ questionId: q.id, message: `Question ${n} needs an answer type.` });
    if (q.type === 'choice') {
      const options = cleanChoiceOptions(q.options);
      if (options.length < L.minOptions) errors.push({ questionId: q.id, message: `Question ${n} needs at least ${L.minOptions} choices.` });
      else if (options.length > L.maxOptions) errors.push({ questionId: q.id, message: `Question ${n} can have up to ${L.maxOptions} choices.` });
      if (options.some((o) => o.length > L.optionMax)) {
        errors.push({ questionId: q.id, message: `Each choice in question ${n} must be ${L.optionMax} characters or fewer.` });
      }
      if (new Set(options.map((o) => o.toLowerCase())).size !== options.length) {
        errors.push({ questionId: q.id, message: `Question ${n} has the same choice twice.` });
      }
    }
  });
  return errors;
}

/** What is saved on the job: trimmed prompts, and choices only on a multiple-choice question. */
export function cleanCustomQuestionsForSave(questions: ReadonlyArray<CustomScreeningQuestion>): CustomScreeningQuestion[] {
  return questions.map((q) => {
    const base: CustomScreeningQuestion = { id: q.id, type: q.type, prompt: q.prompt.trim(), required: q.required === true };
    return q.type === 'choice' ? { ...base, options: cleanChoiceOptions(q.options) } : base;
  });
}

/** True for a real calendar date written YYYY-MM-DD (no 2026-02-30). */
export function isValidIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < 1900 || y > 2200 || mo < 1 || mo > 12 || d < 1) return false;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-11-02" → "Mon, Nov 2, 2026" (the calendar day as written, never shifted by a time zone). */
export function formatIsoDateForDisplay(value: string): string {
  if (!isValidIsoDate(value)) return value;
  const [y, mo, d] = value.split('-').map(Number);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return `${WEEKDAYS[dt.getUTCDay()]}, ${MONTHS[mo - 1]} ${d}, ${y}`;
}

/** A stored answer to one of the employer's own questions, readable: Yes / No, a date, "4 / 5". */
export function formatCustomAnswer(type: string, answer: unknown): string {
  if (answer === null || answer === undefined || answer === '') return '';
  if (type === 'likert') return `${answer} / 5`;
  if (type === 'yesno') {
    const v = String(answer).trim().toLowerCase();
    return v === 'yes' ? 'Yes' : v === 'no' ? 'No' : String(answer);
  }
  if (type === 'date') return formatIsoDateForDisplay(String(answer));
  return String(answer);
}

/** missing: a required question left blank (as opposed to an answer that does not fit the question). */
export type CustomAnswerCheck = { ok: true; answer: string | number | null } | { ok: false; message: string; missing?: boolean };

/**
 * One answer to one of the employer's own questions, cleaned to its stored form. `answer: null`
 * means "not answered" (fine unless the question is required). Client and server run this same code.
 */
export function checkCustomAnswer(q: CustomScreeningQuestion, raw: unknown): CustomAnswerCheck {
  const L = APPLICATION_LIMITS;
  let answer: string | number | null = null;
  if (q.type === 'likert') {
    const num = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;
    if (raw === undefined || raw === null || raw === '') answer = null;
    else if (Number.isInteger(num) && num >= 1 && num <= 5) answer = num;
    else return { ok: false, message: 'Choose a number from 1 to 5' };
  } else {
    // Answers arrive as text. Anything else (an object, a list) is not an answer to any of these types.
    if (raw !== undefined && raw !== null && typeof raw !== 'string') return { ok: false, message: 'Please answer this question' };
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) answer = null;
    else if (q.type === 'yesno') {
      const v = value.toLowerCase();
      if (v !== 'yes' && v !== 'no') return { ok: false, message: 'Choose Yes or No' };
      answer = v;
    } else if (q.type === 'choice') {
      const options = cleanChoiceOptions(q.options);
      if (!options.includes(value)) return { ok: false, message: 'Choose one of the options' };
      answer = value;
    } else if (q.type === 'date') {
      if (!isValidIsoDate(value)) return { ok: false, message: 'Enter a valid date' };
      answer = value;
    } else if (q.type === 'longtext') {
      if (value.length > L.longTextAnswer) return { ok: false, message: `Keep your answer to ${L.longTextAnswer.toLocaleString('en-US')} characters or fewer` };
      answer = value;
    } else {
      if (value.length > L.textAnswer) return { ok: false, message: `Keep your answer to ${L.textAnswer} characters or fewer` };
      answer = value;
    }
  }
  if (answer === null && q.required === true) return { ok: false, message: 'Please answer this question', missing: true };
  return { ok: true, answer };
}

export type StoredCustomAnswer = { questionId: string; prompt: string; type: CustomQuestionType; answer: string | number };

export type BuiltCustomAnswers =
  | { ok: true; answers: StoredCustomAnswer[] }
  | { ok: false; questionId: string; prompt: string; message: string; missing: boolean };

/**
 * Server-side: the candidate's answers checked against the JOB's own question list (never the
 * client's idea of it). Unknown or extra answers are ignored; the prompt stored is the job's own.
 */
export function buildCustomScreeningAnswers(jobQuestions: unknown, rawAnswers: unknown): BuiltCustomAnswers {
  const questions = normalizeCustomQuestions(jobQuestions, 'strict');
  const answerMap = new Map<string, unknown>();
  if (Array.isArray(rawAnswers)) {
    for (const a of rawAnswers) {
      if (a && typeof a === 'object' && typeof (a as { questionId?: unknown }).questionId === 'string') {
        answerMap.set((a as { questionId: string }).questionId, (a as { answer?: unknown }).answer);
      }
    }
  }
  const answers: StoredCustomAnswer[] = [];
  for (const q of questions) {
    const prompt = q.prompt.slice(0, CUSTOM_QUESTION_LIMITS.promptMax);
    const check = checkCustomAnswer(q, answerMap.get(q.id));
    if (!check.ok) return { ok: false, questionId: q.id, prompt, message: check.message, missing: check.missing === true };
    if (check.answer !== null) answers.push({ questionId: q.id, prompt, type: q.type, answer: check.answer });
  }
  return { ok: true, answers };
}

/* ------------------------------------------------------------------ source + resume */

/** "How did you hear about this job?" — optional, stored as given, shown to the employer. */
export const APPLICATION_SOURCE_OPTIONS = [
  'Arevina job board',
  "Employer's website",
  'Referred by a current employee',
  'Referred by a friend or family member',
  'School or training program',
  'Social media',
  'Job fair or event',
  'Other',
] as const;

/** Resume attached at application time: PDF, Word, RTF or plain text. */
export const APPLICATION_RESUME_EXTENSIONS = ['pdf', 'doc', 'docx', 'rtf', 'txt'] as const;
export type ApplicationResumeExtension = (typeof APPLICATION_RESUME_EXTENSIONS)[number];
export const APPLICATION_RESUME_CONTENT_TYPES: Record<ApplicationResumeExtension, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  rtf: 'application/rtf',
  txt: 'text/plain',
};
export const APPLICATION_RESUME_MAX_BYTES = 10 * 1024 * 1024;

/**
 * What the form sends about the resume: use the one on the profile, or a file the candidate just
 * uploaded for this application (already in Storage under their own application's folder), or the
 * candidate's CONFIRMED Arevina resume ('ai': the server reads profiles/{uid}.generatedResume.confirmed,
 * never the draft, and never anything the client sends), or none.
 */
export type ApplicationResumeChoice =
  | { source: 'profile' }
  | { source: 'ai' }
  | { source: 'upload'; storagePath: string; url: string; filename: string; contentType: string; size: number };

/** The structured Arevina resume (same shape the generator writes; src/lib/resume/resumeSafeguards.ts). */
export type ApplicationResumeShape = {
  name: string;
  location: string;
  summary: string;
  certifications: Array<{ name: string; expiresAt: string | null }>;
  experience: Array<{
    title: string;
    employer: string;
    serviceType: string;
    start: string;
    end: string;
    current: boolean;
    bullets: string[];
  }>;
  skills: string[];
};

/** What is stored on the application and shown to the employer. */
export type StoredApplicationResume = {
  source: 'profile' | 'upload' | 'ai';
  /** '' for 'ai' (the resume travels as text, not as a file). */
  url: string;
  filename: string;
  contentType: string;
  size: number;
  storagePath?: string;
  /** 'ai' only: a snapshot of the candidate's confirmed Arevina resume as it was when they applied. */
  confirmedResume?: {
    resume: ApplicationResumeShape;
    text: string;
    confirmedAt: unknown;
    contractVersion: number;
  };
};

export function resumeExtension(filename: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(filename || '');
  return m ? m[1].toLowerCase() : '';
}

/** A user-facing message when the file cannot be used as a resume, else null. */
export function validateResumeFile(file: { name: string; size: number }): string | null {
  const ext = resumeExtension(file.name);
  if (!(APPLICATION_RESUME_EXTENSIONS as readonly string[]).includes(ext)) {
    return 'Upload your resume as a PDF, Word document (.doc or .docx), RTF or plain text file.';
  }
  if (file.size <= 0) return 'That file is empty.';
  if (file.size > APPLICATION_RESUME_MAX_BYTES) return 'Your resume must be 10 MB or smaller.';
  return null;
}

/** The job's enabled standard questions, in canonical order. Unknown keys are ignored. */
export function enabledStandardQuestions(jobValue: unknown): StandardScreeningQuestion[] {
  if (!Array.isArray(jobValue)) return [];
  const on = new Set(jobValue.filter((k): k is string => typeof k === 'string'));
  return STANDARD_SCREENING_QUESTIONS.filter((q) => on.has(q.key));
}

export function normalizeStandardScreeningKeys(value: unknown): StandardScreeningKey[] {
  return enabledStandardQuestions(value).map((q) => q.key);
}

/* ------------------------------------------------------------------ form values */

/**
 * Contact details on an application (ApplicantStack parity, 2026-10-09): name, full mailing address
 * (address line 1 is `street`, kept under its old key), phone and email. Stored on the application as
 * `applicantContact`, readable only by the candidate and the employer they applied to, and never
 * editable by the employer (firestore.rules applications denylist).
 *
 * No date of birth (2026-10-10): employers never see one on an application; the job's "18 or older"
 * screening question covers age. The EMT profile keeps its own date of birth, used only to match the
 * name and date of birth on credential documents.
 */
export type ApplicationContact = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** Address line 1. */
  street: string;
  /** Address line 2 (apartment, suite, unit). Optional. */
  street2: string;
  city: string;
  state: string;
  zip: string;
};

export type ApplicationQualifications = {
  certifications: string[];
  yearsExperience: string;
  currentEmployer: string;
};

export type ApplicationFormValues = {
  contact: ApplicationContact;
  qualifications: ApplicationQualifications;
  /** Standard question key → answer ("Yes" / "No" / text / a choice). */
  standardAnswers: Record<string, string>;
  /** Employer's custom question id → answer (text, 'yes' / 'no', a choice, YYYY-MM-DD, or 1-5 on a legacy rating). */
  customAnswers: Record<string, string | number>;
  /** Cover letter (optional) — typed or pasted. Stored in the application's `message` field. */
  message: string;
  /** "Referred by" (optional) — who pointed the candidate to this job; employers track referrals with it. */
  referredBy: string;
  /** "How did you hear about this job?" (optional) — one of APPLICATION_SOURCE_OPTIONS. */
  source: string;
};

/** What the form sends to the applyToJob Cloud Function (custom answers travel beside it). */
export type ApplicationSubmissionPayload = Pick<
  ApplicationFormValues,
  'contact' | 'qualifications' | 'standardAnswers' | 'message' | 'referredBy' | 'source'
> & {
  resume?: ApplicationResumeChoice | null;
  /**
   * Yes/no statements. caAmbulanceDriverCertificate: "Do you hold a current California Ambulance Driver
   * Certificate?", asked only on a California job that needs one (jobAsksCaAmbulanceDriverCertificate).
   * The apply-time DOT medical card question was removed 2026-10-10 (nothing medical before an offer).
   */
  attestations?: { caAmbulanceDriverCertificate?: 'yes' | 'no' };
};

/**
 * A California job that needs the California Ambulance Driver Certificate (it lists the certificate, or
 * it still lists the retired DOT medical card item, which California drivers held for that certificate).
 * Outside California the question is never asked. Answering is required, but "no" never blocks applying.
 */
export function jobAsksCaAmbulanceDriverCertificate(job: unknown): boolean {
  const j = (job && typeof job === 'object' ? job : {}) as {
    jobLocation?: { stateCode?: unknown } | null;
    location?: unknown;
    locationState?: unknown;
    certificationRequirements?: { requiredCertifications?: unknown; preferredCertifications?: unknown } | null;
    requiredCertifications?: unknown;
    credentialingRequirements?: { requiredItems?: unknown } | null;
  };
  const text = typeof j.location === 'string' ? j.location : '';
  const state = String(j.jobLocation?.stateCode ?? j.locationState ?? (/,\s*([A-Za-z]{2})\b/.exec(text)?.[1] ?? '')).trim().toUpperCase();
  if (state !== 'CA' && state !== 'CALIFORNIA') return false;
  const certs = [j.certificationRequirements?.requiredCertifications, j.certificationRequirements?.preferredCertifications, j.requiredCertifications]
    .flatMap((v) => (Array.isArray(v) ? v : []))
    .map((v) => String(v).toLowerCase());
  const items = Array.isArray(j.credentialingRequirements?.requiredItems) ? j.credentialingRequirements!.requiredItems as unknown[] : [];
  return certs.some((c) => c.includes('california ambulance driver') || c === 'ca-adc') || items.includes('mec');
}

export const CA_ADC_QUESTION = 'Do you hold a current California Ambulance Driver Certificate?';

export const EMPTY_APPLICATION_FORM: ApplicationFormValues = {
  contact: { firstName: '', lastName: '', email: '', phone: '', street: '', street2: '', city: '', state: '', zip: '' },
  qualifications: { certifications: [], yearsExperience: '', currentEmployer: '' },
  standardAnswers: {},
  customAnswers: {},
  message: '',
  referredBy: '',
  source: '',
};

/** Asterisk = required. The form labels, the step validation and the server all read this list. */
export const APPLICATION_REQUIRED_CONTACT_FIELDS: readonly (keyof ApplicationContact)[] = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'street',
  'city',
  'state',
  'zip',
];

export const APPLICATION_CONTACT_LABELS: Record<keyof ApplicationContact, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  street: 'Address line 1',
  street2: 'Address line 2',
  city: 'City',
  state: 'State',
  zip: 'ZIP code',
};

export const APPLICATION_LIMITS = {
  name: 80,
  email: 200,
  phone: 40,
  street: 200,
  street2: 200,
  city: 80,
  state: 40,
  zip: 12,
  certification: 80,
  maxCertifications: 20,
  yearsExperience: 20,
  currentEmployer: 120,
  textAnswer: 300,
  longTextAnswer: 2000,
  customTextAnswer: 2000,
  /** Cover letter. */
  message: 5000,
  referredBy: 120,
  resumeFilename: 200,
} as const;

export function isRequiredContactField(field: keyof ApplicationContact): boolean {
  return APPLICATION_REQUIRED_CONTACT_FIELDS.includes(field);
}

/* ------------------------------------------------------------------ validation */

export type ApplicationStepId = 'contact' | 'qualifications' | 'questions' | 'review';

/** field id → message. Field ids: contact.<field>, standard.<key>, custom.<id>. */
export type ApplicationErrors = Record<string, string>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

const ZIP_RE = /^\d{5}(-\d{4})?$/;

export function validateContact(contact: ApplicationContact): ApplicationErrors {
  const errors: ApplicationErrors = {};
  for (const field of APPLICATION_REQUIRED_CONTACT_FIELDS) {
    if (!text(contact[field], 500)) errors[`contact.${field}`] = `${APPLICATION_CONTACT_LABELS[field]} is required`;
  }
  const email = text(contact.email, APPLICATION_LIMITS.email);
  if (email && !EMAIL_RE.test(email)) errors['contact.email'] = 'Enter a valid email address';
  const phoneDigits = text(contact.phone, APPLICATION_LIMITS.phone).replace(/\D/g, '');
  if (phoneDigits && phoneDigits.length < 10) errors['contact.phone'] = 'Enter a phone number with area code';
  const zip = text(contact.zip, APPLICATION_LIMITS.zip);
  if (zip && !ZIP_RE.test(zip)) errors['contact.zip'] = 'Enter a 5-digit ZIP code';
  return errors;
}

function standardAnswerValid(q: StandardScreeningQuestion, raw: unknown): string {
  const value = text(raw, q.type === 'longtext' ? APPLICATION_LIMITS.longTextAnswer : APPLICATION_LIMITS.textAnswer);
  if (!value) return '';
  if (q.type === 'yesno') return value === 'Yes' || value === 'No' ? value : '';
  if (q.type === 'choice') return (q.options ?? []).includes(value) ? value : '';
  return value;
}

/**
 * An enabled standard question is a question the employer chose to ask: it needs an answer —
 * except the conditional ones (`optional`), which do not apply to every candidate.
 */
export function validateQuestions(
  values: Pick<ApplicationFormValues, 'standardAnswers' | 'customAnswers'>,
  standard: StandardScreeningQuestion[],
  custom: CustomScreeningQuestion[],
): ApplicationErrors {
  const errors: ApplicationErrors = {};
  for (const q of standard) {
    if (q.optional && !text(values.standardAnswers[q.key], 10)) continue;
    if (!standardAnswerValid(q, values.standardAnswers[q.key])) errors[`standard.${q.key}`] = 'Please answer this question';
  }
  // The employer's own questions: the same check applyToJob runs (buildCustomScreeningAnswers), so
  // a required one needs an answer and every answer given must fit its type.
  for (const q of custom) {
    const check = checkCustomAnswer(q, values.customAnswers[q.id]);
    if (!check.ok) errors[`custom.${q.id}`] = check.message;
  }
  return errors;
}

export function validateApplicationStep(
  step: ApplicationStepId,
  values: ApplicationFormValues,
  standard: StandardScreeningQuestion[],
  custom: CustomScreeningQuestion[],
): ApplicationErrors {
  if (step === 'contact') return validateContact(values.contact);
  if (step === 'questions') return validateQuestions(values, standard, custom);
  if (step === 'review') return { ...validateContact(values.contact), ...validateQuestions(values, standard, custom) };
  return {}; // qualifications: nothing is required — a sparse profile leaves these empty
}

/* ------------------------------------------------------------------ what gets stored */

export type StoredStandardAnswer = { key: StandardScreeningKey; prompt: string; answer: string };

export type StoredApplicationForm = {
  contact: ApplicationContact;
  qualifications: ApplicationQualifications;
  standardScreeningAnswers: StoredStandardAnswer[];
  message: string;
  referredBy: string;
  source: string;
  /** Normalized shape only — the Cloud Function checks the upload really belongs to this application. */
  resume: ApplicationResumeChoice | null;
};

export type SanitizedSubmission = { ok: true; form: StoredApplicationForm } | { ok: false; errors: ApplicationErrors };

/**
 * Server-side: clean an untrusted submission against the JOB's own enabled questions (never the
 * client's idea of them). Prompts are copied from this file, so the employer reads exactly what was
 * asked. Answers are stored verbatim — never interpreted.
 */
export function sanitizeApplicationSubmission(raw: unknown, jobStandardKeys: unknown): SanitizedSubmission {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const c = o.contact && typeof o.contact === 'object' ? (o.contact as Record<string, unknown>) : {};
  const q = o.qualifications && typeof o.qualifications === 'object' ? (o.qualifications as Record<string, unknown>) : {};
  const L = APPLICATION_LIMITS;

  const contact: ApplicationContact = {
    firstName: text(c.firstName, L.name),
    lastName: text(c.lastName, L.name),
    email: text(c.email, L.email),
    phone: text(c.phone, L.phone),
    street: text(c.street, L.street),
    street2: text(c.street2, L.street2),
    city: text(c.city, L.city),
    state: text(c.state, L.state),
    zip: text(c.zip, L.zip),
  };
  const certifications = Array.isArray(q.certifications)
    ? [...new Set(q.certifications.map((x) => text(x, L.certification)).filter(Boolean))].slice(0, L.maxCertifications)
    : [];
  const qualifications: ApplicationQualifications = {
    certifications,
    yearsExperience: text(q.yearsExperience, L.yearsExperience),
    currentEmployer: text(q.currentEmployer, L.currentEmployer),
  };

  const standard = enabledStandardQuestions(jobStandardKeys);
  const answersIn = o.standardAnswers && typeof o.standardAnswers === 'object' ? (o.standardAnswers as Record<string, unknown>) : {};
  const errors: ApplicationErrors = { ...validateContact(contact) };
  const standardScreeningAnswers: StoredStandardAnswer[] = [];
  for (const question of standard) {
    const answer = standardAnswerValid(question, answersIn[question.key]);
    if (answer) standardScreeningAnswers.push({ key: question.key, prompt: question.prompt, answer });
    else if (!question.optional || text(answersIn[question.key], 10)) {
      errors[`standard.${question.key}`] = `An answer is required for: ${question.prompt}`;
    }
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  const source = text(o.source, 80);
  return {
    ok: true,
    form: {
      contact,
      qualifications,
      standardScreeningAnswers,
      message: text(o.message, L.message),
      referredBy: text(o.referredBy, L.referredBy),
      // Only a value the form offers — the employer reads this column down a list of applicants.
      source: (APPLICATION_SOURCE_OPTIONS as readonly string[]).includes(source) ? source : '',
      resume: sanitizeResumeChoice(o.resume),
    },
  };
}

function sanitizeResumeChoice(raw: unknown): ApplicationResumeChoice | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.source === 'profile') return { source: 'profile' };
  if (r.source === 'ai') return { source: 'ai' };
  if (r.source !== 'upload') return null;
  const storagePath = text(r.storagePath, 600);
  const url = text(r.url, 2000);
  const filename = text(r.filename, APPLICATION_LIMITS.resumeFilename);
  const size = typeof r.size === 'number' && Number.isFinite(r.size) && r.size > 0 ? r.size : 0;
  const ext = resumeExtension(filename);
  if (!storagePath || !url || !filename || !size || size > APPLICATION_RESUME_MAX_BYTES) return null;
  if (!(APPLICATION_RESUME_EXTENSIONS as readonly string[]).includes(ext)) return null;
  return {
    source: 'upload',
    storagePath,
    url,
    filename,
    contentType: APPLICATION_RESUME_CONTENT_TYPES[ext as ApplicationResumeExtension],
    size,
  };
}
